import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";

export const Route = createFileRoute("/_authenticated/locations/$id/receipts")({
  component: ReceiptsLayout,
  beforeLoad: ({ location, params }) => {
    const subPath = location.pathname.split("/")[4];
    if (!subPath) {
      throw redirect({
        to: "/locations/$id/receipts/kitchen-ticket",
        params: { id: params.id },
      });
    }
  },
});

const receiptsTabsMap = {
  "kitchen-ticket": "Kitchen Ticket",
  "customer-receipt": "Customer Receipt",
  "customer-bill": "Customer Bill",
} as const;

type ReceiptTab = keyof typeof receiptsTabsMap;
const receiptTabs: ReceiptTab[] = [
  "kitchen-ticket",
  "customer-receipt",
  "customer-bill",
];

function ReceiptsLayout() {
  const { id } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  // Loading state
  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>No Employee Profile</AlertTitle>
        <AlertDescription>
          Your account is not linked to an employee profile. Contact your
          organization admin to set up your employee record with the appropriate
          permissions.
        </AlertDescription>
      </Alert>
    );
  }

  // No permission to view receipt settings
  if (!canViewIntegrations) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to view receipt settings. Contact your
          manager to request access.
        </AlertDescription>
      </Alert>
    );
  }

  // Extract current receipt type from pathname
  const pathParts = location.pathname.split("/");
  const currentTab = pathParts[4] ?? "kitchen-ticket"; // Default to kitchen-ticket

  return (
    <div>
      <h1 className="h2 font-heading text-foreground text-balance">
        Receipt Settings
      </h1>
      <p className="body-sm text-foreground mt-1 text-pretty">
        Configure how receipts are printed for different purposes
      </p>

      <Tabs
        className="mt-6"
        onValueChange={(value) => {
          void navigate({ to: `/locations/${id}/receipts/${value}` });
        }}
        value={currentTab}
      >
        <TabsList className="gap-1">
          {receiptTabs.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {receiptsTabsMap[tab]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-6">
        <Outlet />
      </div>
    </div>
  );
}
